import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  afterNextRender,
  inject,
} from '@angular/core';

@Directive({
  selector: '[libMouseSpotlight]',
  standalone: true,
})
export class MouseSpotlightDirective {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private baseBackgroundImage = 'none';
  private baseBackgroundSize = 'auto';
  private baseBackgroundRepeat = 'repeat';
  private baseBackgroundPosition = '0% 0%';

  /**
   * The size of the spotlight in pixels.
   */
  @Input() spotlightSize = 400;

  /**
   * The opacity of the spotlight.
   */
  @Input() spotlightOpacity = 0.15;

  /**
   * The color of the spotlight.
   */
  @Input() spotlightColor = '255, 255, 255';

  constructor() {
    afterNextRender(() => {
      this.applyInitialStyles();
      this.captureBaseBackgroundImage();
    });
  }

  private applyInitialStyles(): void {
    const computedPosition = getComputedStyle(this.el.nativeElement).position;
    if (!computedPosition || computedPosition === 'static') {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    }
  }

  private captureBaseBackgroundImage(): void {
    const element = this.el.nativeElement;
    const inlineBackground = element.style.backgroundImage?.trim();
    if (inlineBackground) {
      this.baseBackgroundImage = inlineBackground;
    } else {
      const computedBackground = getComputedStyle(element).backgroundImage?.trim();
      this.baseBackgroundImage = computedBackground || 'none';
    }

    const inlineSize = element.style.backgroundSize?.trim();
    const inlineRepeat = element.style.backgroundRepeat?.trim();
    const inlinePosition = element.style.backgroundPosition?.trim();
    if (inlineSize || inlineRepeat || inlinePosition) {
      this.baseBackgroundSize = inlineSize || this.baseBackgroundSize;
      this.baseBackgroundRepeat = inlineRepeat || this.baseBackgroundRepeat;
      this.baseBackgroundPosition = inlinePosition || this.baseBackgroundPosition;
      return;
    }

    const computed = getComputedStyle(element);
    this.baseBackgroundSize = computed.backgroundSize?.trim() || 'auto';
    this.baseBackgroundRepeat = computed.backgroundRepeat?.trim() || 'repeat';
    this.baseBackgroundPosition = computed.backgroundPosition?.trim() || '0% 0%';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const isInside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isInside) {
      this.onMouseLeave();
      return;
    }

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.updateSpotlight(x, y);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.renderer.setStyle(
      this.el.nativeElement,
      'background-image',
      this.baseBackgroundImage
    );
    this.renderer.setStyle(
      this.el.nativeElement,
      'background-size',
      this.baseBackgroundSize
    );
    this.renderer.setStyle(
      this.el.nativeElement,
      'background-repeat',
      this.baseBackgroundRepeat
    );
    this.renderer.setStyle(
      this.el.nativeElement,
      'background-position',
      this.baseBackgroundPosition
    );
  }

  private updateSpotlight(x: number, y: number): void {
    const spotlight = `radial-gradient(circle ${this.spotlightSize}px at ${x}px ${y}px, rgba(${this.spotlightColor}, ${this.spotlightOpacity}), transparent 80%)`;
    if (this.baseBackgroundImage && this.baseBackgroundImage !== 'none') {
      this.renderer.setStyle(
        this.el.nativeElement,
        'background-image',
        `${spotlight}, ${this.baseBackgroundImage}`
      );
      this.renderer.setStyle(
        this.el.nativeElement,
        'background-size',
        `100% 100%, ${this.baseBackgroundSize}`
      );
      this.renderer.setStyle(
        this.el.nativeElement,
        'background-repeat',
        `no-repeat, ${this.baseBackgroundRepeat}`
      );
      this.renderer.setStyle(
        this.el.nativeElement,
        'background-position',
        `0 0, ${this.baseBackgroundPosition}`
      );
      return;
    }

    this.renderer.setStyle(this.el.nativeElement, 'background-image', spotlight);
    this.renderer.setStyle(this.el.nativeElement, 'background-size', '100% 100%');
    this.renderer.setStyle(this.el.nativeElement, 'background-repeat', 'no-repeat');
    this.renderer.setStyle(this.el.nativeElement, 'background-position', '0 0');
  }
}
